import React from 'react'
import request from 'graphql-request'
import Swipeable from 'react-swipeable'
import { rem } from 'polished'
import styled from 'styled-components'
import createDebug from 'debug'

import media, { SmallToMediumScreenOnly, Sticky } from '../../utils/style'
import { Flex, Box } from 'rebass'
import EVENTS from '../../utils/events'
import VehicleListingFilterOptions from '../VehicleListingFilterOptions'
import VehicleListingFilterBar from '../VehicleListingFilterBar'
import VehicleListingFilterResults from '../VehicleListingFilterResults'
import IconClose from '../../vectors/IconClose'
import IconRefresh from '../../vectors/IconRefresh'
import IconArrowDown from '../../vectors/IconArrowDown'
import ContentBar from '../ContentBar'
import { array, string } from 'prop-types'
import { sortBy } from 'lodash'
import ContentModal from '../ContentModal'
import createFaceted, {
  numberType,
  rangeType,
  textType,
  multiType
} from '../../../lib/faceted'
import setHistory from '../../../lib/history-setter'
import LoadingSpinner from '../LoadingSpinner'
import VehicleListingHeader from '../VehicleListingHeader'

const debug = createDebug('vehicle-listing-filters')

const Wrapper = styled(Flex)`
  background-color: ${(props) => props.theme.colors.blue_light};
`

const CloseFilterOptions = styled(Box)`
  position: fixed;
  z-index: 2;
  top: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  &.hidden {
    display: none;
  }
`
CloseFilterOptions.displayName = 'CloseFilterOptions'

const OpenFilterOptions = styled(Sticky)`
  z-index: 10;
  height: ${rem(57)};
  cursor: pointer;
  color: white;
  text-align: center;
  text-transform: uppercase;
  line-height: ${rem(57)};
  font-size: ${(props) => rem(props.theme.fontSizes[1])};
  font-weight: ${(props) => props.theme.weights[1]};
  background-color: ${(props) => props.theme.colors.blue};

  &:hover {
    color: ${(props) => props.theme.colors.blue_light_op70};
  }

  &.hidden {
    display: none;
  }
`
OpenFilterOptions.displayName = 'OpenFilterOptions'

/*************************
 STYLES: Results
 **************************/

const FilteredResults = styled(Box)`
  background-color: ${(props) => props.theme.colors.blue_light};
  align-items: center;
  justify-content: center;
`

/*************************
 STYLES: Sorting
 **************************/

const ArrowDown = styled(IconArrowDown)`
  position: relative;
  top: -2px;
  left: 5px;
  path {
    transition: fill 0.5s ${(props) => props.theme.ease.ease_outexpo};
    fill: white;
  }
`

const ArrowUp = styled(ArrowDown)`
  transform: scaleY(-1);
`

/*************************
 STYLES: Visibility Ctrl
 **************************/

const CloseFilterIconClose = styled(IconClose)`
  cursor: pointer;
  position: absolute;
  width: 100%;
  height: 100%;
  transform: scale(0.75);

  path {
    fill: white;
    transition: fill 0.5s cubic-bezier(0.19, 1, 0.22, 1);
    transform: scale(0.45);
    transform-origin: center;
  }
  &:hover > path {
    fill: ${(props) => props.theme.colors.blue};
  }
`
CloseFilterIconClose.displayName = 'RightCentreIconClose'

const IconClearFilters = styled(IconRefresh)`
  width: 18px;
  transform: scale(0.75) translateY(40%) translateX(40%);
  ${media.LargeScreenUp`
    transform: scale(1) translateY(30%) translateX(40%);
  `};
`
IconClearFilters.displayName = 'IconClearFilters'

const sortVehicles = (vehicles, sort) => {
  let sorted = sortBy(vehicles, sort.property)
  if (sort.direction === 'descending') sorted = sorted.reverse()
  return sorted
}

class VehicleListingFilters extends React.Component {
  constructor(props) {
    super(props)
    this.sort = {
      ascending: {
        property: 'value',
        direction: 'ascending',
        label: 'Low - High',
        icon: <ArrowDown />
      },
      descending: {
        property: 'value',
        direction: 'descending',
        label: 'High - Low',
        icon: <ArrowUp />
      }
    }
    this.createSearchIndex(this.props.vehicles)
    const vehicles = sortVehicles(this.props.vehicles, this.sort.ascending)

    const params =
      typeof window !== 'undefined'
        ? decodeURIComponent(window.location.search)
        : []

    this.state = {
      counts: this.faceted.getFilteredCount(),
      sort: this.sort.ascending,
      hidden: true,
      filteredVehicles: vehicles,
      count: vehicles.length,
      isRecentFavouriteOpen: false,
      recentFavourite: {},
      isInfoOpen: false,
      info: {},
      filter: {},
      filtersProcessed: !params && !params.length
    }

    this.onSort = this.onSort.bind(this)
    this.showFilters = this.showFilters.bind(this)
    this.hideFilters = this.hideFilters.bind(this)
    this.swipingLeft = this.swipingLeft.bind(this)
    this.swipingRight = this.swipingRight.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)
    this.handleRecentFavourite = this.handleRecentFavourite.bind(this)
    this.handleRecentFavouriteDismiss =
      this.handleRecentFavouriteDismiss.bind(this)
    this.handleRecentFavouriteReset = this.handleRecentFavouriteReset.bind(this)
    this.getFilterFromQuery = this.getFilterFromQuery.bind(this)
    this.handleInfoOpen = this.handleInfoOpen.bind(this)
    this.handleInfoClose = this.handleInfoClose.bind(this)
  }

  createSearchIndex(vehicles) {
    const data = vehicles.map((vehicle) => {
      let other = []

      return {
        ...vehicle,
        other,
        keywords: [
          vehicle.registration,
          vehicle.brand,
          vehicle.model,
          vehicle.bodyStyle,
          vehicle.derivative,
          vehicle.locationName,
          vehicle.exterior && vehicle.exterior.join(' '),
          vehicle.interior && vehicle.interior.join(' '),
          vehicle.safety && vehicle.safety.join(' '),
          vehicle.technical && vehicle.technical.join(' '),
          vehicle.manufacturerColour,
          vehicle.features && vehicle.features.join(' ')
        ].join(' ')
      }
    })
    const properties = [
      'brand',
      'model',
      'locationName',
      rangeType('value'),
      rangeType('monthlyPayment'),
      numberType('year'),
      rangeType('mileage'),
      'bodyStyle',
      'fuel',
      'transmissionStyle',
      rangeType('engineSize'),
      'colour',
      numberType('seats'),
      rangeType('roadTax'),
      rangeType('mpg'),
      rangeType('co2'),
      rangeType('bhp'),
      textType('keywords'),
      'onSale'
    ]

    if (data.some((vehicle) => vehicle.other.length > 0)) {
      properties.push(multiType('other'))
    }

    this.faceted = createFaceted({
      data,
      properties
    })
  }

  updateResults(filter, sort) {
    this.faceted.setFilter(filter)
    const filteredVehicles = sortVehicles(this.faceted.getResults(), sort)
    this.setState({
      counts: this.faceted.getFilteredCount(),
      filter: this.faceted.getFilter(),
      filteredVehicles,
      count: filteredVehicles.length,
      filtersProcessed: true
    })
  }

  componentDidMount() {
    this.getFilterFromQuery(() => {
      this.createSearchIndex(this.props.vehicles)
      this.updateResults(this.state.filter, this.state.sort)
    })
  }

  componentWillUnmount() {
    clearTimeout(this.recentFavouriteTimeout)
  }

  async updateStatus() {
    let vehicles = this.props.vehicles
    const query = `
    {
      reservedVehicles {
        _id
        reservedDate
      }
      soldVehicles {
        _id
        registration
        expiredDate
      }
    }`

    try {
      const { reservedVehicles, soldVehicles } = await request(
        `${process.env.GATSBY_API_URL}/graphql`,
        query
      )
      const reservationIndex = reservedVehicles.reduce((collection, value) => {
        collection[value._id] = value.reservedDate
        return collection
      }, {})

      const soldIndex = soldVehicles.reduce((collection, value) => {
        collection[value._id] = value
        return collection
      }, {})

      vehicles = vehicles.map((vehicle) => {
        const sold = soldIndex[vehicle._id]
        vehicle.reservedDate = reservationIndex[vehicle._id]
        vehicle.isSold = !!sold
        vehicle.expiredDate = sold && sold.expiredDate

        return vehicle
      })
    } catch (e) {
      debug(e)
    }

    this.getFilterFromQuery(() => {
      this.createSearchIndex(vehicles)
      this.updateResults(this.state.filter, this.state.sort)
    })
  }

  getFilterFromQuery(afterStateChange) {
    const params = decodeURIComponent(window.location.search)
    if (!params || !params.length) return afterStateChange()

    const filter = params
      .substring(1)
      .split('&')
      .reduce((filters, param) => {
        const [key, value] = param.split('=')
        if (!key || !value) return filters
        const range = key.match(/(.*?)(Min|Max)$/)
        if (range) {
          const [, property, propertyKey] = range
          if (!filters[property]) filters[property] = {}
          filters[property][propertyKey.toLowerCase()] = isNaN(value)
            ? 0
            : +value
        } else {
          filters[key] = value.split(',')
        }
        return filters
      }, {})

    this.setState(
      {
        filter
      },
      typeof afterStateChange === 'function' ? afterStateChange : null
    )
  }

  saveFilter(filter) {
    const formatted = Object.keys(filter)
      .reduce((params, key) => {
        if (Array.isArray(filter[key])) {
          const encoded = filter[key].map(encodeURIComponent)
          params.push(`${key}=${encoded.join(',')}`)
        } else if (typeof filter[key] === 'object') {
          params.push(`${key}Min=${encodeURIComponent(filter[key].min)}`)
          params.push(`${key}Max=${encodeURIComponent(filter[key].max)}`)
        } else {
          params.push(`${key}=${encodeURIComponent(filter[key].toLowerCase())}`)
        }
        return params
      }, [])
      .join('&')

    if (!formatted) {
      setHistory()
    } else {
      const currentUrl = window.location.href
      const urlSplit = currentUrl.split('?')
      const url = `${urlSplit[0]}?${formatted}`
      setHistory(url)
      this.getFilterFromQuery(() => this.updateResults(filter, this.state.sort))
    }
  }

  handleRecentFavourite(details) {
    this.setState({ recentFavourite: details, isRecentFavouriteOpen: true })
  }

  handleRecentFavouriteDismiss() {
    this.recentFavouriteTimeout = setTimeout(
      this.handleRecentFavouriteReset,
      500
    )
  }

  handleRecentFavouriteReset() {
    this.setState({ recentFavourite: {}, isRecentFavouriteOpen: false })
  }

  onFilterChange(filter) {
    this.updateResults(filter, this.state.sort)
    this.saveFilter(this.faceted.getFilter())
  }

  onSort(e) {
    e && e.preventDefault()
    const newSort =
      this.state.sort.direction === 'descending'
        ? this.sort.ascending
        : this.sort.descending
    this.updateResults(this.state.filter, newSort)
    this.setState({ sort: newSort })
  }

  showFilters(e) {
    e && e.preventDefault()
    this.setState({ hidden: false })
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0)
      window.dispatchEvent(new Event(EVENTS.CONTENT.DIMMER_ON))
    }
  }

  hideFilters(e) {
    e && e.preventDefault()
    this.setState({ hidden: true })
    if (typeof window !== 'undefined')
      window.dispatchEvent(new Event(EVENTS.CONTENT.DIMMER_OFF))
  }

  /* MARK: Swipeable events */
  swipingLeft(e, absX) {
    if (absX >= EVENTS.SWIPE.X) {
      this.hideFilters()
    }
  }

  swipingRight(e, absX) {
    if (absX >= EVENTS.SWIPE.X) {
      this.showFilters()
    }
  }

  updatePriceValues(_sliderState) {
    this.setState({
      price: {
        ...this.state.price,
        values: _sliderState.values
      }
    })
  }

  handleInfoOpen(info) {
    this.setState({
      info: { ...info },
      isInfoOpen: true
    })
  }

  handleInfoClose() {
    this.setState({
      info: {},
      isInfoOpen: false
    })
  }

  render() {
    return (
      <Swipeable
        onSwipingLeft={this.swipingLeft}
        onSwipingRight={this.swipingRight}
      >
        {this.state.isRecentFavouriteOpen && (
          <ContentBar
            success
            dismissible
            autodismiss
            sticky
            onDismiss={this.handleRecentFavouriteDismiss}
          >
            <strong>
              <a href={this.state.recentFavourite.path}>
                {this.state.recentFavourite.brand}{' '}
                {this.state.recentFavourite.model}
              </a>
            </strong>{' '}
            has been added to your{' '}
            <strong>
              <a href={`${process.env.GATSBY_PORTAL_URL}/favourites`}>
                favourites list
              </a>
            </strong>
          </ContentBar>
        )}
        <Wrapper flexWrap="wrap">
          <FilteredResults width={[12 / 12, null, 8 / 12, 9 / 12]} order={2}>
            <VehicleListingHeader
              title="Trusted Car Sales"
              description="Finding a quality is easy when you know where to look – and we’ve got plenty to choose from. All our used cars for sale are thoroughly checked to ensure they meet our high standards and will always have a minimum six-month MOT. You can choose a used car on finance or buy it outright, with plenty of impressive deals and discounts available."
            />
            <VehicleListingFilterBar
              sortType={this.state.sort}
              page={{
                total: this.state.count,
                type: this.props.vehicleType
              }}
              onSort={this.onSort}
            />

            {!this.state.filtersProcessed && <LoadingSpinner />}

            {this.state.filtersProcessed && (
              <VehicleListingFilterResults
                onInfoOpen={this.handleInfoOpen}
                vehicles={this.state.filteredVehicles}
                vehicleType={this.props.vehicles[0].category}
                onRecentFavourite={this.handleRecentFavourite}
              />
            )}
          </FilteredResults>

          <VehicleListingFilterOptions
            hidden={this.state.hidden}
            vehicles={this.props.vehicles}
            filteredVehicles={this.state.filteredVehicles}
            onChange={this.onFilterChange}
            filter={this.state.filter}
            counts={this.state.counts}
            vehicleType={this.props.vehicleType}
            hideFilters={this.hideFilters}
            page={{
              total: this.state.count,
              type: this.props.vehicleType
            }}
          />

          <SmallToMediumScreenOnly>
            <CloseFilterOptions
              width={[2 / 12, 1 / 12]}
              className={this.state.hidden ? 'hidden' : ''}
              onClick={(evt) => this.hideFilters(evt, this)}
            />
            <OpenFilterOptions
              width={12 / 12}
              left
              top
              onClick={(evt) => this.showFilters(evt, this)}
            >
              Filter used {this.props.vehicleType}
            </OpenFilterOptions>
          </SmallToMediumScreenOnly>

          <ContentModal
            title={this.state.info.title}
            slim
            open={this.state.isInfoOpen}
            onClose={this.handleInfoClose}
          >
            {this.state.info.children}
          </ContentModal>
        </Wrapper>
      </Swipeable>
    )
  }
}

VehicleListingFilters.defaultProps = {
  vehicles: [],
  vehicleType: 'cars'
}
VehicleListingFilters.propTypes = {
  vehicles: array,
  vehicleType: string
}

export default VehicleListingFilters
